import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  CardContent,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetAllUserListQuery,
  useGetAllQueueQuery,
  useGetCallDetailsReportQuery,
  useLazyGetCallDetailsReportDownloadQuery,
  useLazyGetCallDetailsReportfetchrecordingQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";
import CsvDownloader from "react-csv-downloader";
import { AUTH_TOKEN } from "../../../../constants";

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const isCurrentDateRange = (from: string, to: string) => {
  const today = getTodayDateString();
  return from === today && to === today;
};

type ReportMode = "live" | "historical" | null;

/** Seconds to wait after recording sync API succeeds before refetching call details */
const RECORDING_SYNC_WAIT_SEC = 60;

function formatCountdownMmSs(totalSeconds: number) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

const ContactDetailReport = () => {
  const navigate = useNavigate();
  const [reportType, setReportType] = useState("Inbound"); // Default to Inbound
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [selectedAssignee, setSelectedAssignee] = useState<any>([]);
  const { data: userData, isLoading: userLoading } = useGetAllUserListQuery({});
  const {
    data: queueData,
    isLoading: queueLoading,
    refetch,
  } = useGetAllQueueQuery(undefined, {
    skip: reportType === "Outbound",
  });
  const [params, setParams] = useState<any>(null);
  const [reportMode, setReportMode] = useState<ReportMode>(null);
  const [dataRows, setDataRows] = useState<any[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [reportFetching, setReportFetching] = useState(false);
  const [totalRowsFromApi, setTotalRowsFromApi] = useState<number | null>(null);

  const {
    data: callsDetailsData,
    isLoading: callsDetailsLoading,
    isFetching: callsDetailsFetching,
    refetch: refetchCallDetails,
  } = useGetCallDetailsReportQuery(params, {
    skip: !params || reportMode !== "live",
  });

  const [triggerCallDetailsDownload, { isFetching: downloadLoading }] =
    useLazyGetCallDetailsReportDownloadQuery();

  const [triggerFetchRecording, { isFetching: isFetchingRecording }] =
    useLazyGetCallDetailsReportfetchrecordingQuery();

  const downloaderRef = useRef(null);

  const [recordingSyncSecondsLeft, setRecordingSyncSecondsLeft] = useState<
    number | null
  >(null);
  const recordingSyncTimerRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const clearRecordingSyncTimer = () => {
    if (recordingSyncTimerRef.current) {
      clearInterval(recordingSyncTimerRef.current);
      recordingSyncTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearRecordingSyncTimer();
  }, []);

  const reportingBaseUrl =
    process.env.REACT_APP_REPORTING_SERVER_URL || "https://reporting.bluewhirl.org";

  const [selectedQueues, setSelectedQueues] = useState<any>([]);
  const [options, setOptions] = useState<any>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  // const [selectQueue, setSelectQueue] = useState<any>('')
  const [isAllAssigneeSelected, setIsAllAssigneeSelected] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });

  const columnsInbound: GridColDef[] = [
    {
      field: "sr_no",
      headerName: "Sr.no",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "account_name",
      headerName: "Department/Team name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "customer_name",
      headerName: "Customer name",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "phone_number",
      headerName: "Customer number",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "service_number",
      headerName: "Service number",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "campaign",
      headerName: "Queue name",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "agent_role",
      headerName: "User role",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "agent_name",
      headerName: "User name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "xlite_no",
      headerName: "User extension",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "start_date",
      headerName: "Call start date",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "start_time",
      headerName: "Call start time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "duration",
      headerName: "Total handled time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    //
    {
      field: "transfer_duration",
      headerName: "Transfer duration",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "conference_duration",
      headerName: "Conference duration",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },

    //
    {
      field: "ringing_duration",
      headerName: "Ringing duration",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "conversation_duration",
      headerName: "Conversation duration",
      minWidth: 160,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hold_duration",
      headerName: "Total hold time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "ivr_duration",
      headerName: "IVR duration",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_cause",
      headerName: "Call outcome",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_sub_cause",
      headerName: "Secondary call outcome",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_remark",
      headerName: "Call outcome remarks",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_source",
      headerName: "Hangup source",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "end_date",
      headerName: "Call end date",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "call_end_time",
      headerName: "Call end time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "disposition_time",
      headerName: "After call work",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "recording",
      headerName: "Call recordings",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.value !== "No data found" ? (
          <a href={params.value} target="_blank" rel="noopener noreferrer">
            Play
          </a>
        ) : (
          "No data found"
        ),
    },
  ];

  const columnsOutbound: GridColDef[] = [
    {
      field: "sr_no",
      headerName: "Sr.no",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "account_name",
      headerName: "Department/Team name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "customer_name",
      headerName: "Customer name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "phone_number",
      headerName: "Customer number",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "service_number",
      headerName: "Service number",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "campaign",
      headerName: "Campaign name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "agent_role",
      headerName: "User role",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "agent_name",
      headerName: "User name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "xlite_no",
      headerName: "User extension",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "agent_call_status",
      headerName: "Call outcome",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "start_date",
      headerName: "Call start date",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "start_time",
      headerName: "Call start time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "duration",
      headerName: "Total handled time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    // {
    //   field: "transfer_duration",
    //   headerName: "Transfer Duration",
    //   align: "center",
    //   headerAlign: "center",
    // },
    // {
    //   field: 'conference_duration',
    //   headerName: "Conference Duration",
    //   align: "center",
    //   headerAlign: "center",
    // },

    {
      field: "ringing_duration",
      headerName: "Dialing duration",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "conversation_duration",
      headerName: "Conversation duration",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hold_duration",
      headerName: "Total hold time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_cause",
      headerName: "Call outcome",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_sub_cause",
      headerName: "Secondary call outcome",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_remark",
      headerName: "Call outcome remarks",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    // {
    //   field: "hangup_source",
    //   headerName: "Hangup Source",
    //   align: "center",
    //   headerAlign: "center",
    // },

    {
      field: "end_date",
      headerName: "Call end date",
      align: "center",
      headerAlign: "center",
    },

    {
      field: "end_time",
      headerName: "Call end time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "disposition_time",
      headerName: "After call Work",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    // {
    //   field: "handletime",
    //   headerName: "Handle Time",
    //   minWidth: 140,
    //   align: "center",
    //   headerAlign: "center",
    // },
    {
      field: "recording",
      headerName: "Call recordings",
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.value !== "No data found" ? (
          <a href={params.value} target="_blank" rel="noopener noreferrer">
            Play
          </a>
        ) : (
          "No data found"
        ),
    },
  ];

  const handleReportTypeChange: any = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setReportType(event.target.value as string);
    setDataRows([]);
    setParams(null);
    setReportMode(null);
    setTotalRowsFromApi(null);
    if (event.target.value === "Inbound") {
      refetch();
    }
  };

  const handleDateFromChange = (event: any) => {
    setDateFrom(event.target.value);
  };

  const handleDateToChange = (event: any) => {
    setDateTo(event.target.value);
  };

  // const handleChange: any = (event: React.ChangeEvent<{ value: unknown }>) => {
  //   setSelectedAssignee(event.target.value as string);
  // };
  // const handleQueueChange = (event: any) => {
  //   setSelectQueue(event.target.value);
  // };

  useEffect(() => {
    if (queueData) {
      const transformedQueOptions = queueData.map((item) => ({
        label: item.name, // Use 'name' for the display text
        value: item.id, // Use 'id' as the unique identifier
      }));

      setOptions(transformedQueOptions);
    }
  }, [queueData]);

  useEffect(() => {
    if (userData) {
      const transformedAssigneeOptions = userData.map((user) => ({
        label: user.username, // Use 'name' for the display text
        value: user.id, // Use 'id' as the unique identifier
      }));

      setUserOptions(transformedAssigneeOptions);
    }
  }, [userData]);

  useEffect(() => {
    const dropdownHeadings = document.querySelectorAll(".dropdown-heading");
    dropdownHeadings.forEach((heading: any) => {
      heading.style.height = "50px";
    });
  }, []);

  // useEffect(() => {

  //   const transformedData = userData?.map((user) => ({
  //     id: user.id,
  //     username: user.username,
  //   }));
  //   if (Array.isArray(userData)) {
  //     setTheUser(transformedData);
  //   } else {
  //     setTheUser([]); // Fallback to an empty array if it's not an array
  //   }
  // }, [userData]);

  // useEffect(() => {
  //   const transformedData = queueData?.map((queue) => ({
  //     id: queue.id,
  //     name: queue.name,
  //   }));
  //   if (queueData && Array.isArray(queueData)) {
  //     setAllQueue(transformedData)
  //   }
  // }, [queueData]);

  const handleSubmit = () => {
    if (!dateFrom) {
      toast.error('Please select "From Date"');
      return;
    }
    if (!dateTo) {
      toast.error('Please select "To Date"');
      return;
    }
    if (selectedAssignee.length === 0) {
      toast.error("Please select Assignee");
      return;
    }

    // if (!selectQueue && reportType === "Inbound") {
    //   toast.error('Please select Queue');
    //   return;
    // }
    if (selectedQueues.length === 0 && reportType === "Inbound") {
      toast.error("Please select a queue");
      return;
    }
    const selectedQueueLabels = selectedQueues
      .map((queueId) => {
        const queue = options.find((q) => q.value === queueId);
        return queue ? queue.label : null;
      })
      .filter(Boolean);
    const selectedAssigneeIds = selectedAssignee
      .map((assigneeId) => {
        const assignee = userOptions.find((q) => q.value === assigneeId);
        return assignee ? assignee.value : null;
      })
      .filter((id) => id != null);

    const isLive = isCurrentDateRange(dateFrom, dateTo);
    setReportMode(isLive ? "live" : "historical");
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });

    const queryParams: any = {
      gte: dateFrom,
      lte: dateTo,
      call_type: reportType === "Inbound" ? "inbound" : "outbound",
    };

    if (isLive) {
      queryParams.queue = selectedQueueLabels;
      queryParams.user_id = selectedAssigneeIds;
    } else {
      queryParams.page = 1;
      queryParams.page_size = paginationModel.pageSize;
      queryParams.user_id = selectedAssigneeIds.join(",");
      if (reportType === "Inbound" && selectedQueueLabels.length > 0) {
        queryParams.queue = selectedQueueLabels.join(",");
      }
    }

    const hadExistingParams = params != null;
    flushSync(() => {
      setParams(queryParams);
    });
    if (isLive && hadExistingParams) {
      void refetchCallDetails();
    }
  };

  function formatDate(datetime: any) {
    if (!datetime) return "No data found";
    const dateObj = new Date(datetime);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = String(dateObj.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  }

  function formatTime(datetime: any) {
    if (!datetime) return "No data found";
    const dateObj = new Date(datetime);
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const seconds = String(dateObj.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }

  const formatDuration = (duration: any) => {
    if (!duration || typeof duration !== "string") return "-";
    const parts = duration.split(":").map((part: string) => part.padStart(2, "0"));
    return parts.join(":");
  };

  const mapCallDetailItemToRow = (
    item: any,
    index: number,
    isInbound: boolean,
    rowOffset: number
  ) => {
    const sr = rowOffset + index + 1;
    const commonData: any = {
      id: sr,
      sr_no: sr,
      account_name: item.contacts?.[0]?.account?.name || "-",
      customer_name: item.contacts?.[0]?.name || "-",
      phone_number: item.customer_phone_no || "-",
      service_number: item.called_from_phone_no || "-",
      campaign: item.campaign_name || "-",
      agent_role: item.agent_role || "-",
      agent_name: item.agent_name || "-",
      xlite_no: item.extension_no || "-",
      start_time: formatTime(item.call_start_time_display) || "-",
      start_date: formatDate(item.call_start_time_display) || "-",
      duration: item.duration ? formatDuration(item.duration) : "-",
      ringing_duration: item.ringing_seconds || "-",
      conversation_duration: item.conversation_duration
        ? formatDuration(item.conversation_duration)
        : "",
      hold_duration: item.hold_duration_display
        ? formatDuration(item.hold_duration_display)
        : "",
      hangup_cause: item.dispose_remark || "-",
      hangup_sub_cause: item.sub_dispose_remark || "-",
      hangup_remark: item.remarks || "-",
      hangup_source: item.hangup_source || "-",
      end_date: formatDate(item.call_end_time_display) || "-",
      call_end_time: formatTime(item.call_end_time) || "-",
      disposition_time: item.acw_duration_display
        ? formatDuration(item.acw_duration_display)
        : "-",
      handle_time: item.call_duration || "-",
      recording: item.recording_link || "-",
      call_duration: item.call_duration || "-",
      end_time: formatTime(item.call_end_time_display) || "-",
    };

    if (isInbound) {
      return {
        ...commonData,
        transfer_duration: item.transfer_duration
          ? formatDuration(item.transfer_duration)
          : "-",
        conference_duration: item.conference_duration
          ? formatDuration(item.conference_duration)
          : "-",
        campaign_ivr_duration: item.campaign_ivr_msg || "-",
        ivr_duration: item.ivr_duration1 || "-",
        language: item.language || "-",
      };
    }
    return {
      ...commonData,
      agent_call_status: item.agent_outbound_call_status || "-",
    };
  };

  useEffect(() => {
    if (reportMode !== "live") return;

    if (!Array.isArray(callsDetailsData)) {
      setDataRows([]);
      setLocalLoading(false);
      return;
    }

    const isInbound = reportType === "Inbound";
    const transformedRows = callsDetailsData.map((item, index) =>
      mapCallDetailItemToRow(item, index, isInbound, 0)
    );
    setDataRows(transformedRows);
    setLocalLoading(false);
  }, [callsDetailsData, reportMode, reportType]);

  useEffect(() => {
    if (params && reportMode === "live") {
      setLocalLoading(true);
    }
  }, [params, reportMode]);

  useEffect(() => {
    if (!params || reportMode !== "historical") {
      if (reportMode !== "historical") {
        setDataRows([]);
        setTotalRowsFromApi(null);
      }
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN);
    if (!token) {
      toast.error("Session expired or not logged in. Please sign in again.");
      return;
    }

    const controller = new AbortController();
    setLocalLoading(true);
    setReportFetching(true);

    const queryParams: Record<string, string | number | boolean> = {
      from_date: params.gte,
      to_date: params.lte,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      download: false,
      call_type: params.call_type,
    };
    if (params.user_id != null && params.user_id !== "") {
      queryParams.user_id = params.user_id;
    }
    if (params.queue != null && params.queue !== "") {
      queryParams.queue = params.queue;
    }

    axios
      .get(`${reportingBaseUrl}/telephony/call-history`, {
        params: queryParams,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const data = res?.data;
        const results = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : [];
        const totalRows =
          data?.total_rows ?? data?.count ?? data?.total ?? results.length;
        setTotalRowsFromApi(
          typeof totalRows === "number" ? totalRows : results.length
        );
        const serverPage = data?.current_page ?? data?.page ?? params.page ?? 1;
        const serverPageSize = data?.page_size ?? params.page_size ?? 20;
        const apiPage = Number(serverPage);
        const pageSizeNum = Number(serverPageSize);
        setPaginationModel({
          page: Math.max(0, apiPage - 1),
          pageSize: pageSizeNum,
        });
        const rowOffset = (apiPage - 1) * pageSizeNum;
        const isInbound = params.call_type !== "outbound";
        if (results.length > 0) {
          setDataRows(
            results.map((item: any, index: number) =>
              mapCallDetailItemToRow(item, index, isInbound, rowOffset)
            )
          );
        } else {
          setDataRows([]);
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        toast.error(
          err?.response?.data?.message ||
            "Failed to fetch Call details report"
        );
        setDataRows([]);
      })
      .finally(() => {
        setLocalLoading(false);
        setReportFetching(false);
      });

    return () => controller.abort();
  }, [params, reportingBaseUrl, reportMode]);

  const handleDownload = async () => {
    if (!params) {
      toast.error("Please apply filters and submit before generating the report.");
      return;
    }

    const downloadPayload: Record<string, string | number> = {
      from_date: params.gte,
      to_date: params.lte,
      page: 1,
      page_size: totalRowsFromApi ?? 99999,
      call_type: params.call_type,
    };
    if (params.user_id != null && params.user_id !== "") {
      downloadPayload.user_id = params.user_id;
    }
    if (params.queue != null && params.queue !== "") {
      downloadPayload.queue = params.queue;
    }

    try {
      await triggerCallDetailsDownload(downloadPayload).unwrap();
      toast.success("Report generation in progress. Download from exported report section once ready");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to queue report generation.");
    }
  };

  const resetFilters = () => {
    clearRecordingSyncTimer();
    setRecordingSyncSecondsLeft(null);
    setDateFrom("");
    setDateTo("");
    setParams(null);
    setReportMode(null);
    setSelectedQueues([]);
    setSelectedAssignee([]);
    setDataRows([]);
    setTotalRowsFromApi(null);
    setPaginationModel({ page: 0, pageSize: 20 });
  };

  const isRecordingSyncCooldown =
    recordingSyncSecondsLeft !== null && recordingSyncSecondsLeft > 0;

  const getRecordingRefreshPayload = () => {
    if (!params) return null;
    if (reportMode === "live") {
      return {
        gte: params.gte,
        lte: params.lte,
        call_type: params.call_type,
        user_id: params.user_id,
        ...(params.queue != null ? { queue: params.queue } : {}),
      };
    }
    return {
      gte: params.gte,
      lte: params.lte,
      call_type: params.call_type,
      user_id: params.user_id,
      ...(params.queue != null && params.queue !== ""
        ? { queue: params.queue }
        : {}),
    };
  };

  const handleRefreshReport = async () => {
    if (!params) {
      toast.warning(
        "Submit your filters first. If recordings or report rows are still missing, try refresh."
      );
      return;
    }
    if (isRecordingSyncCooldown) return;

    const refreshPayload = getRecordingRefreshPayload();
    if (!refreshPayload) return;

    try {
      const raw = (await triggerFetchRecording(refreshPayload).unwrap()) as {
        message?: string;
        data?: { message?: string };
      };
      const syncMessage =
        (typeof raw?.message === "string" && raw.message) ||
        (typeof raw?.data?.message === "string" && raw.data.message) ||
        null;
      toast.success(syncMessage ?? "Recording sync started.");

      clearRecordingSyncTimer();
      setRecordingSyncSecondsLeft(RECORDING_SYNC_WAIT_SEC);

      recordingSyncTimerRef.current = setInterval(() => {
        setRecordingSyncSecondsLeft((prev) => {
          if (prev === null || prev <= 0) return null;
          if (prev <= 1) {
            clearRecordingSyncTimer();
            if (reportMode === "live") {
              void refetchCallDetails().then(() => {
                toast.success("Call details refreshed.");
              });
            } else {
              setParams((current) => (current ? { ...current } : null));
              toast.success("Call details refreshed.");
            }
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      const msg =
        err?.data?.message ||
        err?.data?.detail ||
        err?.error ||
        "Failed to refresh recordings.";
      toast.error(typeof msg === "string" ? msg : "Failed to refresh recordings.");
    }
  };

  const ITEM_HEIGHT = 60;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 150,
      },
    },
  };

  const handlePaginationChange = (newPaginationModel: any) => {
    setPaginationModel(newPaginationModel);
    if (!params || reportMode === "live") return;
    setLocalLoading(true);
    setParams({
      ...params,
      page: newPaginationModel.page + 1,
      page_size: newPaginationModel.pageSize,
    });
  };

  const showFullPageSpinner =
    userLoading ||
    queueLoading ||
    (reportMode === "historical" && reportFetching) ||
    (reportMode === "live" &&
      (callsDetailsLoading ||
        (!!params && callsDetailsFetching && dataRows.length === 0)));

  const handleQueueChange = (event) => {
    const {
      target: { value },
    } = event;
    if (value.includes("all")) {
      setIsAllSelected(!isAllSelected);
      setSelectedQueues(
        isAllSelected ? [] : options.map((option) => option.value)
      );
    } else {
      setSelectedQueues(value);
      setIsAllSelected(value.length === options.length);
    }
  };

  const handleAssigneeChange = (event) => {
    const {
      target: { value },
    } = event;
    if (value.includes("all")) {
      setIsAllAssigneeSelected(!isAllAssigneeSelected);
      setSelectedAssignee(
        isAllAssigneeSelected ? [] : userOptions.map((option) => option.value)
      );
    } else {
      setSelectedAssignee(value);
      setIsAllAssigneeSelected(value.length === userOptions.length);
    }
  };

  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        {/* Card Header */}
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          {/* Back button */}
          {/* <img
            src="https://insurance.circleonecrm.com/assets/arrow_back.svg"
            alt="Back"
            style={{ marginBottom: "-2px", cursor: "pointer" }}
            className="ng-star-inserted"
            onClick={() => {
              navigate("/reports/telephony/reports-list");
            }}
          /> */}
          <button
            className="btn btn-link mb-2"
            onClick={() => {
              navigate("/reports/telephony/reports-list");
            }}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{
                fontSize: "20px",
                color: "black",
                cursor: "pointer",
              }}
            ></i>
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3"> Call details</span>
        </div>

        {/* Toolbar */}
        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10"
          style={{ marginLeft: "30px" }}
        >
          <div className="w-100 mw-150px">
            <TextField
              id="date-from"
              label="From"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  max: new Date().toISOString().split("T")[0], // Restrict to today or earlier
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                },
              }}
              fullWidth
              value={dateFrom}
              onChange={handleDateFromChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <TextField
              id="date-to"
              label="To"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  max: new Date().toISOString().split("T")[0],
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                },
              }}
              fullWidth
              value={dateTo}
              onChange={handleDateToChange}
            />
          </div>

          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="select-type">Select Type</InputLabel>
              <Select
                labelId="select-type"
                id="select-type"
                label="Select Type"
                value={reportType}
                onChange={handleReportTypeChange}
              >
                <MenuItem value="Inbound">Inbound</MenuItem>
                <MenuItem value="Outbound">Outbound</MenuItem>
              </Select>
            </FormControl>
          </div>
          {/* <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="Assignee-select-label">Assignee</InputLabel>
              <Select labelId="Assignee-select-label" id="Assignee-select" label="Assignee" MenuProps={MenuProps} value={selectedAssignee}
                onChange={handleChange}>
                <MenuItem key="all" value="All">
                  All
                </MenuItem>
                {theUser?.map((assignee: any) => (
                  <MenuItem key={assignee.id} value={assignee.id}>
                    {assignee.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div> */}
          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="assignee-select-label">Assignee</InputLabel>
              <Select
                labelId="assignee-select-label"
                id="assignee-select"
                multiple
                value={selectedAssignee}
                onChange={handleAssigneeChange}
                input={<OutlinedInput label="Assignee" />}
                renderValue={(selected) => {
                  if (isAllAssigneeSelected) {
                    return <div>All</div>;
                  }
                  return (
                    <div>
                      {selected
                        .map((assigneeId) => {
                          const assignee = userOptions.find(
                            (q) => q.value === assigneeId
                          );
                          return assignee ? assignee.label : null;
                        })
                        .join(", ")}
                    </div>
                  );
                }}
                MenuProps={MenuProps}
              >
                <MenuItem
                  value="all"
                  onClick={() =>
                    setIsAllAssigneeSelected(!isAllAssigneeSelected)
                  }
                >
                  <Checkbox
                    checked={isAllAssigneeSelected}
                    indeterminate={
                      selectedAssignee.length > 0 && !isAllAssigneeSelected
                    }
                  />
                  <ListItemText primary="All" />
                </MenuItem>
                {userOptions.map((assignee) => (
                  <MenuItem key={assignee.value} value={assignee.value}>
                    <Checkbox
                      checked={selectedAssignee.indexOf(assignee.value) > -1}
                    />
                    <ListItemText primary={assignee.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          {reportType === "Inbound" && (
            // <div className="w-100 mw-150px">
            //   <FormControl fullWidth>
            //     <InputLabel id="Queue-select-label">Queue</InputLabel>
            //     <Select
            //       labelId="Queue-select-label"
            //       id="Queue-select"
            //       label="Queue"
            //       value={selectQueue}
            //       onChange={handleQueueChange}
            //       MenuProps={MenuProps}
            //     >
            //       {" "}
            //       <MenuItem key="all" value="All">
            //         All
            //       </MenuItem>
            //       {allQueue?.map((queue: any) => (

            //         <MenuItem key={queue.id} value={queue.id}>{queue.name}</MenuItem>
            //       ))}
            //     </Select>
            //   </FormControl>
            // </div>
            <div className="w-100 mw-150px">
              <FormControl fullWidth>
                <InputLabel id="queue-select-label">Select Queues</InputLabel>
                <Select
                  labelId="queue-select-label"
                  id="queue-select"
                  multiple
                  value={selectedQueues}
                  onChange={handleQueueChange}
                  input={<OutlinedInput label="Select Queues" />}
                  renderValue={(selected) => {
                    if (isAllSelected) {
                      return <div>All</div>;
                    }
                    return (
                      <div>
                        {selected
                          .map((queueId) => {
                            const queue = options.find(
                              (q) => q.value === queueId
                            );
                            return queue ? queue.label : null;
                          })
                          .join(", ")}
                      </div>
                    );
                  }}
                  MenuProps={MenuProps}
                >
                  <MenuItem
                    value="all"
                    onClick={() => setIsAllSelected(!isAllSelected)}
                  >
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={
                        selectedQueues.length > 0 && !isAllSelected
                      }
                    />
                    <ListItemText primary="All" />
                  </MenuItem>
                  {options.map((queue) => (
                    <MenuItem key={queue.value} value={queue.value}>
                      <Checkbox
                        checked={selectedQueues.indexOf(queue.value) > -1}
                      />
                      <ListItemText primary={queue.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}

          <div className="ms-auto d-flex gap-3" style={{ marginRight: "20px" }}>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit
            </button>

            {reportMode === "live" ? (
              <button
                className="btn btn-primary"
                disabled={!params || dataRows.length === 0}
              >
                <CsvDownloader
                  ref={downloaderRef}
                  filename={`${reportType}-call-details-report`}
                  datas={dataRows}
                  columns={(reportType === "Inbound"
                    ? columnsInbound
                    : columnsOutbound
                  ).map((col) => ({
                    id: col.field,
                    displayName: col.headerName,
                  }))}
                  separator=","
                  wrapColumnChar={`"`}
                >
                  Download
                </CsvDownloader>
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={!params || downloadLoading}
              >
                {downloadLoading ? "Loading..." : "Download"}
              </button>
            )}

            <button className="btn btn-link" onClick={resetFilters}>
              <i className="bi bi-funnel fs-2 text-dark"></i>
            </button>

            <Tooltip
              title={
                isRecordingSyncCooldown && recordingSyncSecondsLeft != null
                  ? `Wait ${formatCountdownMmSs(recordingSyncSecondsLeft)} — the report will refresh automatically.`
                  : "If you are missing call recordings or report details, click refresh. After sync, wait 1 minute for the report to refresh."
              }
              arrow
              placement="top"
              enterDelay={300}
            >
              <span className="d-inline-flex">
                <button
                  type="button"
                  className="btn btn-link p-1 d-inline-flex flex-column align-items-center justify-content-center gap-0"
                  onClick={handleRefreshReport}
                  disabled={
                    !params ||
                    localLoading ||
                    (reportMode === "historical" && reportFetching) ||
                    (reportMode === "live" &&
                      (callsDetailsLoading || callsDetailsFetching)) ||
                    isFetchingRecording ||
                    isRecordingSyncCooldown
                  }
                  aria-label={
                    isRecordingSyncCooldown && recordingSyncSecondsLeft != null
                      ? `Refresh report, ${formatCountdownMmSs(recordingSyncSecondsLeft)} remaining`
                      : "Refresh report"
                  }
                >
                  {isFetchingRecording ||
                  ((reportMode === "historical" && reportFetching) ||
                    (reportMode === "live" && callsDetailsFetching)) &&
                  !isRecordingSyncCooldown ? (
                    <span
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                      style={{ width: "1.25rem", height: "1.25rem" }}
                    />
                  ) : (
                    <i
                      className="bi bi-arrow-clockwise fs-2 text-primary"
                      aria-hidden
                    />
                  )}
                  {isRecordingSyncCooldown && recordingSyncSecondsLeft != null && (
                    <span
                      className="small text-primary fw-semibold lh-1 mt-1"
                      style={{
                        fontVariantNumeric: "tabular-nums",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {formatCountdownMmSs(recordingSyncSecondsLeft)}
                    </span>
                  )}
                </button>
              </span>
            </Tooltip>
          </div>
        </div>

        <CardContent style={{ width: "100%" }}>
          {showFullPageSpinner ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={dataRows}
              columns={
                reportType === "Inbound" ? columnsInbound : columnsOutbound
              }
              pagination
              paginationMode={
                reportMode === "historical" ? "server" : "client"
              }
              rowCount={
                reportMode === "historical" ? totalRowsFromApi ?? 0 : undefined
              }
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              loading={
                reportMode === "live"
                  ? localLoading || callsDetailsFetching
                  : localLoading
              }
              disableColumnMenu
              pageSizeOptions={[5, 10, 15, 20]}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                  "&:last-child": {
                    justifyContent: "flex-end",
                  },
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-columnsContainer": {
                  backgroundColor: "lightgray",
                },
                // Target the pagination select and actions by stable component classes
                "& .MuiTablePagination-select, .MuiTablePagination-actions": {
                  marginBottom: "10px",
                },
                // Target the DataGrid overlay wrapper with its stable class
                "& .MuiDataGrid-overlayWrapper": {
                  height: "50px",
                },
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default ContactDetailReport;
